import { useEffect, useRef } from 'react';

export interface ShortcutBinding {
  /** Normalised form: `mod+enter`, `mod+shift+enter`, `shift+enter`, `mod+/`. */
  readonly combo: string;
  readonly run: () => void;
  /** Allow the binding to fire while a text field has focus. Default false. */
  readonly allowInInput?: boolean;
}

function normalise(event: KeyboardEvent): string {
  const parts: string[] = [];
  if (event.metaKey || event.ctrlKey) parts.push('mod');
  if (event.altKey) parts.push('alt');
  if (event.shiftKey) parts.push('shift');

  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key.toLowerCase();
  parts.push(key);
  return parts.join('+');
}

function inEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  // CodeMirror renders a contenteditable div inside .cm-editor.
  return target.closest('.cm-editor') !== null;
}

/**
 * Application-level shortcuts.
 *
 * Editor-scoped bindings (run, comment) live in the CodeMirror keymap so they
 * work with the cursor inside the editor; these are the global ones. Bindings
 * are read from a ref so a re-render never detaches the listener mid-keypress.
 */
export function useShortcuts(bindings: readonly ShortcutBinding[]): void {
  const ref = useRef(bindings);
  ref.current = bindings;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const combo = normalise(event);
      const editable = inEditableTarget(event.target);

      for (const binding of ref.current) {
        if (binding.combo !== combo) continue;
        if (editable && !binding.allowInInput) continue;
        event.preventDefault();
        event.stopPropagation();
        binding.run();
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}
